import { getQaAllowedScopesForLabelType, getQaTechRepFromSampleKey } from '@resistapp/common/qa-label-utils';
import { QaLabelScope, QaLabelScopeEntry, QaLabelType, WellDebugData } from '@resistapp/common/types';
import { groupBy, uniq } from 'lodash';

export interface QaConcreteRepSelection {
  sampleKey: string;
  sampleId: number;
  assay: string;
  techRep: 1 | 2 | 3;
}

export interface QaSelectionState {
  isAllChipImplicit: boolean;
  availableScopes: QaLabelScope[];
  entriesByScope: Record<QaLabelScope, QaLabelScopeEntry[]>;
  selectedConcreteReps: QaConcreteRepSelection[];
}

export function isImplicitWholeChipQaLabelAllowed(labelType: QaLabelType): boolean {
  return labelType === QaLabelType.ISSUE || labelType === QaLabelType.OTHER;
}

export function buildQaSelectionState(
  debugData: WellDebugData[],
  selectedSamples: string[] | null,
  selectedGroups: string[] | null,
): QaSelectionState {
  const allConcreteReps = debugData.reduce<QaConcreteRepSelection[]>((acc, datum) => {
    const sampleId = datum.cts.sampleId;
    const techRep = getQaTechRepFromSampleKey(datum.cts.sample);
    if (sampleId == null || techRep == null) {
      return acc;
    }

    acc.push({
      sampleKey: datum.cts.sample,
      sampleId,
      assay: datum.cts.assay,
      techRep,
    });
    return acc;
  }, []);
  const selectedConcreteReps = allConcreteReps.filter(
    rep =>
      (selectedSamples == null || selectedSamples.includes(rep.sampleKey)) &&
      (selectedGroups == null || selectedGroups.includes(rep.assay)),
  );

  const entriesByScope: Record<QaLabelScope, QaLabelScopeEntry[]> = {
    [QaLabelScope.CHIP]: [],
    [QaLabelScope.TECHREP]: [],
    [QaLabelScope.TRIPLICATE]: [],
    [QaLabelScope.SAMPLE]: [],
    [QaLabelScope.ASSAY]: [],
  };

  if (selectedConcreteReps.length === 0) {
    return {
      isAllChipImplicit: selectedSamples == null && selectedGroups == null,
      availableScopes: [],
      entriesByScope,
      selectedConcreteReps,
    };
  }

  const selectedTriplicates = groupBy(selectedConcreteReps, rep => `${rep.sampleId}|${rep.assay}`);
  const allTriplicates = groupBy(allConcreteReps, rep => `${rep.sampleId}|${rep.assay}`);
  const selectedSamplesById = groupBy(selectedConcreteReps, rep => String(rep.sampleId));
  const allSamplesById = groupBy(allConcreteReps, rep => String(rep.sampleId));
  const selectedAssaysById = groupBy(selectedConcreteReps, rep => rep.assay);
  const allAssaysById = groupBy(allConcreteReps, rep => rep.assay);

  const techrepEntries = buildTechrepEntries(selectedConcreteReps);
  if (techrepEntries.length > 0) {
    entriesByScope[QaLabelScope.TECHREP] = techrepEntries;
  }

  if (selectedConcreteReps.length > 0 && selectedConcreteReps.length === allConcreteReps.length) {
    entriesByScope[QaLabelScope.CHIP] = [{ sampleId: null, assay: null, techRep: null }];
  }

  if (Object.entries(selectedTriplicates).every(([key, reps]) => reps.length === allTriplicates[key].length)) {
    entriesByScope[QaLabelScope.TRIPLICATE] = Object.keys(selectedTriplicates)
      .sort()
      .map(key => {
        const [sampleIdRaw, assay] = key.split('|');
        return {
          sampleId: Number(sampleIdRaw),
          assay,
          techRep: null,
        };
      });
  }

  if (
    Object.entries(selectedSamplesById).every(([sampleId, reps]) => reps.length === allSamplesById[sampleId].length)
  ) {
    entriesByScope[QaLabelScope.SAMPLE] = Object.keys(selectedSamplesById)
      .sort((a, b) => Number(a) - Number(b))
      .map(sampleId => ({
        sampleId: Number(sampleId),
        assay: null,
        techRep: null,
      }));
  }

  if (Object.entries(selectedAssaysById).every(([assay, reps]) => reps.length === allAssaysById[assay].length)) {
    entriesByScope[QaLabelScope.ASSAY] = Object.keys(selectedAssaysById)
      .sort()
      .map(assay => ({
        sampleId: null,
        assay,
        techRep: null,
      }));
  }

  return {
    isAllChipImplicit: selectedSamples == null && selectedGroups == null,
    availableScopes: (Object.entries(entriesByScope) as Array<[QaLabelScope, QaLabelScopeEntry[]]>)
      .filter(([, entries]) => entries.length > 0)
      .map(([scope]) => scope),
    entriesByScope,
    selectedConcreteReps,
  };
}

export function getDefaultQaScope(
  availableScopes: QaLabelScope[],
  selectedSamples: string[] | null,
  selectedGroups: string[] | null,
  labelType: QaLabelType = QaLabelType.OTHER,
): QaLabelScope | null {
  const allowedScopes = new Set(getQaAllowedScopesForLabelType(labelType));
  const preferredOrder =
    selectedSamples == null && selectedGroups != null
      ? [QaLabelScope.CHIP, QaLabelScope.ASSAY, QaLabelScope.TRIPLICATE, QaLabelScope.TECHREP, QaLabelScope.SAMPLE]
      : selectedSamples != null && selectedGroups == null
        ? [QaLabelScope.CHIP, QaLabelScope.SAMPLE, QaLabelScope.TRIPLICATE, QaLabelScope.TECHREP, QaLabelScope.ASSAY]
        : [QaLabelScope.CHIP, QaLabelScope.TRIPLICATE, QaLabelScope.TECHREP, QaLabelScope.SAMPLE, QaLabelScope.ASSAY];

  return preferredOrder.find(scope => availableScopes.includes(scope) && allowedScopes.has(scope)) ?? null;
}

function buildTechrepEntries(selectedConcreteReps: QaConcreteRepSelection[]): QaLabelScopeEntry[] {
  const sampleIds = uniq(selectedConcreteReps.map(rep => rep.sampleId));
  const assays = uniq(selectedConcreteReps.map(rep => rep.assay));
  if (sampleIds.length !== 1 || assays.length !== 1) {
    return [];
  }

  return selectedConcreteReps
    .slice()
    .sort((a, b) => a.techRep - b.techRep)
    .map(rep => ({
      sampleId: rep.sampleId,
      assay: rep.assay,
      techRep: rep.techRep,
    }));
}
